import parsePhoneNumber from "libphonenumber-js";
import { z } from "zod";

import { i18n } from "@lingui/core";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

/**
 * Messages are translated when the schema runs, not when it is written: a
 * descriptor handed straight to zod is read for its source text, which a
 * production build strips. The form parses on every change, so the text
 * follows the locale in force.
 */
const text = (descriptor: MessageDescriptor): string =>
  // Outside a running app, a test for instance, no locale has been activated
  // and the source text is the honest answer rather than a crash.
  i18n.locale ? i18n._(descriptor) : (descriptor.message ?? descriptor.id);

/** What a check zod owns takes: a function it calls when it fails. */
const fails = (descriptor: MessageDescriptor) => ({
  error: () => text(descriptor),
});

// export const contactPhonePlaceSchema = z.enum([
//   "MAIN",
//   "HOME,VOICE",
//   "WORK,VOICE",
//   "CELL",
//   "FAX",
//   "HOME,FAX",
//   "WORK,FAX",
//   "PAGER",
//   "VOICE",
// ]);

// export const contactEmailPlaceSchema = z.enum(["HOME", "WORK", "OTHER"]);

// export const contactUrlPlaceSchema = z.enum(["HOME", "WORK", "OTHER"]);

// export const contactAddressPlaceSchema = z.enum(["HOME", "WORK", "OTHER"]);

export const contactPhoneNumberSchema = z.string().transform((arg, ctx) => {
  const phone = parsePhoneNumber(arg, {
    // The default country is used when the phone number doesn't include a country code.
    defaultCountry: "CH",

    // set to false to require that the whole string is exactly a phone number,
    // otherwise, it will search for a phone number anywhere within the string
    extract: false,
  });

  if (phone && phone.isValid()) {
    // console.log("Valid phone number:", phone.formatInternational());
    // return phone.formatInternational();
    return phone.number;
  }

  ctx.addIssue({
    code: "custom",
    message: text(msg`Invalid phone number`),
  });

  return z.NEVER;
});

const place = z
  .string()
  .trim()
  .min(1, fails(msg`Place is required`));

export const contactPhoneSchema = z.object({
  place: place,
  number: contactPhoneNumberSchema,
});

export const contactEmailSchema = z.object({
  place: place,
  address: z.email(),
});

export const contactUrlSchema = z.object({
  place: place,
  url: z.url(),
});

/**
 * `postOfficeBox` has no control of its own: it rides along so that saving an
 * address does not drop what the API sent. `state` is the API's name for what
 * the form shows as the region.
 */
export const contactTypedAddressSchema = z.object({
  place: place,
  postOfficeBox: z.string().optional(),
  extendedAddress: z.string().optional(),
  street: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  typedAddressId: z.number().optional(),
});

export const contactRelationSchema = z.object({
  id: z.number().optional(),
  // type: z.enum(["direct", "contact", "available"]),
  type: z.string().optional(),
  displayName: z.string().optional(),
  entity: z.string().optional(),
  icon: z.string().optional(),
  route: z.string().optional(),
  newUrl: z.string().optional(),
  modalUrl: z.string().optional(),
  redirect: z.boolean().optional(),
});

const titles = [
  "",
  "Madame",
  "Monsieur",
  "Docteur",
  "Maître",
  "Professeur",
  "Madame et Monsieur",
  "Administration communale",
  "Accueil de Jour des Enfants",
  "Association",
  "Société Coopérative",
  "Entreprise Générale",
  "Cabinet médical",
  "Société immobilière",
  "Fiduciaire",
] as const;

const name = z
  .string()
  .min(2, fails(msg`Minimum 2 characters`))
  .max(50, fails(msg`Maximum 50 characters`))
  .regex(/^[^0-9]/u, fails(msg`Must not start with a number`))
  .regex(
    /^[\p{L}\p{M}0-9 .,'\-&()]*$/u,
    fails(msg`Only letters, numbers, spaces and .,'- & () are allowed`),
  )
  .optional();

export const contactCategorySchema = z.object({
  key: z.string(),
  name: z.string(),
  type: z.enum(["all", "new", "uncategorized", "vcard"]),
  contactCount: z.number().nullable(),
});

export const contactSchema = z
  .object({
    id: z.number(),
    cardId: z.string().optional(),
    title: z.enum(titles).default(""),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    displayName: z.string().optional(),
    avatarUrl: z.url().optional().nullable().default(null),
    hasPhoto: z.boolean().default(false),
    isOrganization: z.boolean().default(false),
    organization: z.string().optional(),
    organizationSubName: z.string().optional(),
    birthday: z.date().optional(),
    categories: z.array(contactCategorySchema).default([]),
    relations: z.array(contactRelationSchema).default([]),
    phones: z.array(contactPhoneSchema).default([]),
    emails: z.array(contactEmailSchema).default([]),
    urls: z.array(contactUrlSchema).default([]),
    typedAddresses: z.array(contactTypedAddressSchema).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.isOrganization) {
      const result = name.safeParse(data.organization);

      if (!result.success) {
        result.error.issues.forEach((issue) =>
          ctx.addIssue({ ...issue, path: ["organization"] }),
        );
      }
    } else {
      for (const field of ["firstName", "lastName"] as const) {
        const result = name.safeParse(data[field]);

        if (!result.success) {
          result.error.issues.forEach((issue) =>
            ctx.addIssue({ ...issue, path: [field] }),
          );
        }
      }
    }
  });
